<script setup>
import AppButton from '#/general/AppButton.vue'
import { getRawFormData, resetForm, useForm, validateForm } from 'vue3-form'
import AboutUsInput from 'components/aboutUs/AboutUsInput.vue'
import AboutUsTextarea from 'components/aboutUs/AboutUsTextarea.vue'
import ContactUsMutation from '../../graphql/contactUsMutation.gql'
import { useToast } from 'vue-toast-notification'
import { useAnalyzer } from '#/analyzer/useAnalyzer'
import { useHubspot } from 'src/composables/useHubspot'
import { useGA } from '#/useGA'
import { useShared } from '#/useShared'

const { sharedApolloClient } = useAnalyzer()
const form = useForm({
  lastName: { rules: ['lastName'] },
  firstName: { rules: ['firstName'] },
  email: { rules: ['required', 'email'] },
  phoneNumber: { rules: ['required', 'phoneNumber'] },
  message: { rules: ['required'] }
})
const $toast = useToast({ position: 'bottom' })
const { event } = useGA()
const { identify } = useHubspot()
const { interpretErrorMessage } = useShared()

async function attemptSubmit () {
  try {
    if (!validateForm(form)) {
      $toast.open({ type: 'info', message: 'Bitte füllen Sie alle Pflichtfelder aus.' })
      return
    }
    const res = await sharedApolloClient.value.query({
      query: ContactUsMutation,
      variables: {
        ...getRawFormData(form)
      },
      fetchPolicy: 'no-cache'
    })

    if (res.data && res.data && res.data.submitContactForm && res.data.submitContactForm.success) {
      $toast.open({ type: 'success', message: 'Vielen Dank für Ihr Interesse. Gerne halten wir Sie auf dem Laufenden und melden uns, sobald wir Ihr Anliegen bearbeitet haben.' })
      event('b2c_conversion_1', { eventCategory: 'B2C', eventLabel: 'About Us Contact form submitted' })
      identify({
        email: form.value.fields.email.value,
        firstname: form.value.fields.firstName.value,
        lastname: form.value.fields.lastName.value,
        phone: form.value.fields.phoneNumber.value,
        challenges_b2c: form.value.fields.message.value,
        web_form_type: 'About Us',
        user_persona: 'B2C',
        ...(res.data.submitContactForm.updateReferrer ? { rp_source_url: document.referrer } : {})
      }, '/b2c/analyzer/')
    } else {
      $toast.open({ type: 'warning', message: 'Leider haben wir ein Serverproblem auf unserer Seite festgestellt. Bitte kontaktiere uns auf hallo@raumpioniere.io, damit wir dieses rasch beheben können.' })
    }
    resetForm(form)
  } catch (e) {
    resetForm(form)
    $toast.open({ type: 'warning', message: interpretErrorMessage(e) })
    console.log(e)
    console.log(JSON.parse(JSON.stringify(e)))
  }
}

</script>

<template lang="pug">
.us--contact
  .us--contact-title
    | Nehmen Sie jetzt Kontakt mit uns auf und
    br
    | bleiben Sie stets auf dem Laufenden:
  form.us--contact-form(@submit.prevent="attemptSubmit")
    about-us-input(
      v-model:value="form.fields.firstName.value"
      :errors="form.fields.firstName.errors"
      placeholder="Vorname"
      name="First Name"
    )
    about-us-input(
      v-model:value="form.fields.lastName.value"
      :errors="form.fields.lastName.errors"
      placeholder="Nachname"
      name="Family Name"
    )
    about-us-input(
      v-model:value="form.fields.phoneNumber.value"
      :errors="form.fields.phoneNumber.errors"
      placeholder="Telefon"
      name="phone number"
    )
    about-us-input(
      v-model:value="form.fields.email.value"
      :errors="form.fields.email.errors"
      placeholder="E-Mail Adresse"
      name="email"
    )
  about-us-textarea(
    v-model:value="form.fields.message.value"
    :errors="form.fields.message.errors"
    title="Nachricht"
    placeholder="Hier Nachricht eingeben."
    name="message"
  )
  app-button.us--contact-cta(
    label="Die Raumpioniere kontaktieren"
    padding="0 44px"
    @clicked="attemptSubmit"
  )
</template>

<style lang="sass">
.us--contact
  display: flex
  flex-direction: column
  align-items: center
  @media screen and (min-width: 1024px)
    margin-top: 110px
  @media screen and (max-width: 1024px)
    margin-top: 60px
  .us--contact-title
    font-weight: 500
    line-height: 130%
    text-align: center
    color: #22223C
    max-width: 850px
    @media screen and (min-width: 1024px)
      font-size: 34px
    @media screen and (max-width: 1024px)
      font-size: 24px
  .us--contact-form
    max-width: 850px
    width: 100%
    display: grid
    @media screen and (min-width: 1024px)
      grid-template-columns: 1fr 1fr
      margin-top: 100px
      grid-gap: 40px
    @media screen and (max-width: 1024px)
      margin-top: 40px
      grid-gap: 30px
  .us--contact-cta
    @media screen and (min-width: 1024px)
      margin-top: 100px
      margin-bottom: 132px
    @media screen and (max-width: 1024px)
      margin-top: 50px
      margin-bottom: 60px
</style>
